import React, { useMemo } from "react";
import { Select } from "components/Select/Select";
import { InputCardTitle as InputCardTitleBase } from "components/InputCards/InputCardTitle";
import { TooltipIcon } from "components/InputCards/TooltipIcon";
import { convertRalToRgb } from "components/InputCards/ralToRgb";
import { useRalColorPicker } from "./useRalColorPicker";
import type {
  SelectColorInputProps,
  SelectColorInputComponentProps,
  ColorOption,
} from "./SelectColorInput.types";

// InputCardTitle is a JS component; cast to apply correct prop types
const InputCardTitle = InputCardTitleBase as React.ComponentType<{
  title?: string | null;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}>;

export const SelectColorInput = ({
  option,
  disabled,
  onChange,
  part,
}: SelectColorInputProps) => {
  const options: ColorOption[] = useMemo(() => {
    const result =
      option?.signatures
        ?.filter(
          (signature) =>
            signature?.state !== null &&
            signature?.state !== undefined &&
            signature?.state < 2,
        )
        .map((signature) => {
          const suffix =
            signature?.value !== signature?.title
              ? ` (${signature?.presentation})`
              : "";
          return {
            label: `${suffix} ${signature?.title}`,
            value: signature?.key ?? "",
            description: signature?.description ?? "",
            state: signature?.state ?? 4, // Default to 4 (fully disabled) if state is missing
            isDisabled: signature?.state !== 0,
          };
        }) || [];

    return result.reverse();
  }, [option.signatures]);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === option.currentSignature?.key),
    [options, option.currentSignature],
  );

  return (
    <SelectColorInputComponent
      options={options}
      label={option.title ?? ""}
      partKey={part.key ?? ""}
      optionKey={option.key ?? ""}
      value={selectedOption}
      handleChange={(opt) => onChange(opt.value)}
      disabled={disabled}
    />
  );
};

const SelectColorInputComponent = ({
  label,
  options,
  value,
  disabled,
  handleChange,
  description,
  handlePartClick,
  optionKey,
  partKey,
}: SelectColorInputComponentProps) => {
  const {
    isLoading,
    setIsLoading,
    firstTwo,
    thirdDigit,
    fourthDigit,
    firstTwoDigitOptions,
    thirdDigitOptions,
    fourthDigitOptions,
    onFirstTwoChange,
    onThirdDigitChange,
    onFourthDigitChange,
  } = useRalColorPicker(options, value, handleChange);

  const selectedColor = useMemo(
    () => convertRalToRgb(value?.value),
    [value?.value],
  );

  return (
    <>
      <TooltipIcon description={description} />
      <InputCardTitle
        title={label}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
      <div className="d-flex gap-4">
        <div style={{ minWidth: "170px" }}>
          <Select
            name={`config-${partKey}-${optionKey}-firstTwo`}
            options={firstTwoDigitOptions}
            value={
              firstTwoDigitOptions.find((opt) => opt.value === firstTwo) ?? null
            }
            disabled={disabled}
            onChange={onFirstTwoChange}
            testId={`select-${partKey}-${optionKey}-firstTwo`}
            onMenuOpen={handlePartClick}
            menuShouldScrollIntoView={false}
          />
        </div>
        <div style={{ minWidth: "80px" }}>
          <Select
            name={`config-${partKey}-${optionKey}-thirdDigit`}
            options={thirdDigitOptions}
            value={
              thirdDigitOptions.find((opt) => opt.value === thirdDigit) ?? null
            }
            disabled={disabled}
            onChange={onThirdDigitChange}
            testId={`select-${partKey}-${optionKey}-thirdDigit`}
            onMenuOpen={handlePartClick}
            menuShouldScrollIntoView={false}
          />
        </div>
        <div style={{ minWidth: "80px" }}>
          <Select
            name={`config-${partKey}-${optionKey}-fourthDigit`}
            options={fourthDigitOptions}
            value={
              fourthDigitOptions.find((opt) => opt.value === fourthDigit) ??
              null
            }
            disabled={disabled}
            onChange={onFourthDigitChange}
            testId={`select-${partKey}-${optionKey}-fourthDigit`}
            onMenuOpen={handlePartClick}
            menuShouldScrollIntoView={false}
          />
        </div>
      </div>
      <div className="py-5">
        {value?.label} - {value?.description}
      </div>
      <div
        className="py-5"
        style={{ height: "100px", backgroundColor: selectedColor ?? "#f0f0f0" }}
      />
    </>
  );
};
